import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  CalendarDays, Clock, Loader2, RefreshCw, AlertTriangle, X,
  CheckCircle2, XCircle, Hourglass, Ban, FileText, ChevronDown, ChevronUp,
  CalendarOff, Eye,
} from 'lucide-react';
import { LeaveBalanceView, LeaveRequestView, LeaveRequestStatus, LeaveTypeView, UpcomingHolidayView } from '../../../../models/attendance';
import * as LeavesApi from '../../../../api/Attendance/leaves';
import { extractApiError } from '../../../../utils/errors';
import { BalanceWarningBadge, resolveBalanceWarnings } from '../../../../components/leaves/BalanceWarningBadge';
import RequestDetailModal from '../../../Attendance/LeaveApprovals/components/RequestDetailModal';
import Attendance from '../index';
import UserProfile from '../../index';

interface Props {
  userId: string;
  role: string;
  showBalances?: boolean;
}

function formatDate(dateStr: string | Date): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function daysUntil(dateStr: string): number {
  const now = new Date();
  const target = new Date(dateStr);
  return Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

const STATUS_CONFIG: Record<LeaveRequestStatus, { icon: React.ElementType; bg: string; text: string }> = {
  [LeaveRequestStatus.Approved]: { icon: CheckCircle2, bg: 'bg-green-100', text: 'text-green-700' },
  [LeaveRequestStatus.Pending]: { icon: Hourglass, bg: 'bg-amber-100', text: 'text-amber-700' },
  [LeaveRequestStatus.Rejected]: { icon: XCircle, bg: 'bg-red-100', text: 'text-red-700' },
  [LeaveRequestStatus.Cancelled]: { icon: Ban, bg: 'bg-gray-100', text: 'text-gray-600' },
  [LeaveRequestStatus.CancellationPending]: { icon: Clock, bg: 'bg-orange-100', text: 'text-orange-700' },
};

const r = (n: number) => Math.round(n * 100) / 100;

const EmployeeLeavesAndHolidays = ({ userId, role, showBalances = true }: Props) => {
  const { t } = useTranslation('leaves');
  const { id } = useParams();
  const profileUserId = id ?? userId;

  const [balances, setBalances] = useState<LeaveBalanceView[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequestView[]>([]);
  const [holidays, setHolidays] = useState<UpcomingHolidayView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllRequests, setShowAllRequests] = useState(false);
  const [statusFilter, setStatusFilter] = useState<LeaveRequestStatus | ''>('');
  const [leaveTypes, setLeaveTypes] = useState<LeaveTypeView[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequestView | null>(null);
  const [loadingRequestId, setLoadingRequestId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Neither the route param nor the Redux user is guaranteed to be present on the first render
    // after a hard refresh. Without this guard the id gets stringified into the request path
    // (".../undefined"), which the API answers as a permission denial — previously a 401 that
    // force-logged the user out. Show an error instead of firing the request.
    if (!profileUserId) {
      setBalances([]);
      setLeaveRequests([]);
      setError(t('employeeLeaves.missingEmployee'));
      setLoading(false);
      return;
    }
    try {
      const [balanceRes, requestsRes, holidaysRes, leaveTypesRes] = await Promise.all([
        LeavesApi.getBalancePerUser(profileUserId),
        LeavesApi.getLeaveRequestsPerUser(profileUserId),
        LeavesApi.getUpcomingHolidays(90),
        LeavesApi.getCompanyLeaveTypes(),
      ]);

      if (balanceRes?.errors?.length) {
        setError(balanceRes.errors[0]?.message || t('employeeLeaves.loadError'));
      } else {
        setBalances(balanceRes.payload ?? []);
      }

      if (!requestsRes?.errors?.length && requestsRes.payload) {
        setLeaveRequests(requestsRes.payload.leaveRequests ?? []);
      }

      if (!holidaysRes?.errors?.length && holidaysRes.payload) {
        setHolidays(holidaysRes.payload);
      }

      if (!leaveTypesRes?.errors?.length && leaveTypesRes.payload) {
        setLeaveTypes(leaveTypesRes.payload);
      }
    } catch (err) {
      setError(extractApiError(err, t('employeeLeaves.loadError')));
    } finally {
      setLoading(false);
    }
  }, [profileUserId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetails = async (leaveRequestId: number) => {
    setLoadingRequestId(leaveRequestId);
    try {
      const result = await LeavesApi.getLeaveRequest(leaveRequestId);
      if (result.errors.length === 0) {
        setSelectedRequest(result.payload);
      }
    } catch { /* ignore */ }
    setLoadingRequestId(null);
  };

  const filteredRequests = statusFilter
    ? leaveRequests.filter(r => r.status === statusFilter)
    : leaveRequests;

  const displayedRequests = showAllRequests ? filteredRequests : filteredRequests.slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="employee-leaves-loading">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
        <span className="ltr:ml-3 rtl:mr-3 text-muted-foreground">{t('employeeLeaves.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employee-leaves-and-holidays-page">
      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Leave Balances Section */}
      {showBalances && (
        <div className="bg-card border border-border rounded-lg" data-testid="employee-balances-section">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-teal-600" />
              <h3 className="text-md font-semibold text-foreground">
                {t('employeeLeaves.leaveBalances')}
              </h3>
            </div>
            <button
              onClick={fetchData}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
              title={t('employeeLeaves.refresh')}
              data-testid="employee-leaves-refresh"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          {balances.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <CalendarDays className="h-12 w-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground text-sm">{t('employeeLeaves.noBalances')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {balances.map((b) => {
                const usedDays = b.used ?? 0;
                const usagePercent = b.eligibility > 0 ? Math.min((usedDays / b.eligibility) * 100, 100) : 0;
                const warnings = resolveBalanceWarnings({
                  balance: b.balance,
                  warningThreshold: b.warningThreshold ?? undefined,
                  carryForwardBalance: b.carryForwardBalance,
                  carryForwardExpiry: b.carryForwardExpiry ?? undefined,
                });
                const progressColor = usagePercent > 80
                  ? 'bg-red-500' : usagePercent > 60
                  ? 'bg-amber-500' : 'bg-teal-500';

                return (
                  <div
                    key={b.leaveTypeId}
                    className="bg-background border border-border rounded-lg p-4 hover:shadow-sm transition-shadow"
                    data-testid={`balance-card-${b.leaveTypeId}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-foreground">{t(b.leaveType)}</span>
                    </div>

                    {/* Balance display */}
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-2xl font-bold text-foreground">{r(b.balance)}</span>
                      <span className="text-sm text-muted-foreground">/ {r(b.eligibility)}</span>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full h-2 bg-muted rounded-full mb-2">
                      <div
                        className={`h-full rounded-full transition-all ${progressColor}`}
                        style={{ width: `${usagePercent}%` }}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mb-2">
                      {t('employeeLeaves.used')}: {r(usedDays)} {t('employeeLeaves.days')}
                    </div>

                    {/* Carry forward */}
                    {b.carryForwardBalance > 0 && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-1">
                        <Clock className="h-3 w-3" />
                        {t('employeeLeaves.carryForward')}: {r(b.carryForwardBalance)}
                        {b.carryForwardExpiry && (
                          <span className="text-muted-foreground/70">
                            ({daysUntil(b.carryForwardExpiry) > 0
                              ? t('userLeavePolicies.expiresIn', { days: daysUntil(b.carryForwardExpiry) })
                              : t('userLeavePolicies.expired')})
                          </span>
                        )}
                      </div>
                    )}

                    {/* Warnings */}
                    {warnings.map((status) => (
                      <BalanceWarningBadge
                        key={status}
                        status={status}
                        balance={b.balance}
                        carryForwardDays={b.carryForwardBalance}
                        expiryDate={b.carryForwardExpiry}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Leave History Section */}
      <div className="bg-card border border-border rounded-lg" data-testid="employee-leave-history-section">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600" />
            <h3 className="text-md font-semibold text-foreground">
              {t('employeeLeaves.leaveHistory')}
            </h3>
            <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
              {filteredRequests.length}
            </span>
          </div>

          {/* Status filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as LeaveRequestStatus | '')}
            className="text-sm border border-border rounded-md px-2 py-1 bg-background text-foreground"
            data-testid="employee-leaves-status-filter"
          >
            <option value="">{t('employeeLeaves.allStatuses')}</option>
            {Object.values(LeaveRequestStatus).map(s => (
              <option key={s} value={s}>{t(`employeeLeaves.status.${s}`)}</option>
            ))}
          </select>
        </div>

        {leaveRequests.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground text-sm">{t('employeeLeaves.noLeaveRequests')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="employee-leave-history-table">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-start py-3 px-4 text-muted-foreground font-medium">{t('employeeLeaves.leaveType')}</th>
                    <th className="text-start py-3 px-4 text-muted-foreground font-medium">{t('employeeLeaves.period')}</th>
                    <th className="text-end py-3 px-4 text-muted-foreground font-medium">{t('employeeLeaves.daysCol')}</th>
                    <th className="text-start py-3 px-4 text-muted-foreground font-medium">{t('employeeLeaves.statusCol')}</th>
                    <th className="text-start py-3 px-4 text-muted-foreground font-medium">{t('employeeLeaves.comments')}</th>
                    <th className="text-center py-3 px-4 text-muted-foreground font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {displayedRequests.map((req) => {
                    const statusCfg = STATUS_CONFIG[req.status] ?? STATUS_CONFIG[LeaveRequestStatus.Pending];
                    const StatusIcon = statusCfg.icon;
                    return (
                      <tr
                        key={req.id}
                        className="border-b border-border/50 hover:bg-muted/30 cursor-pointer"
                        data-testid={`leave-request-row-${req.id}`}
                        onClick={() => handleViewDetails(req.id)}
                      >
                        <td className="py-3 px-4 font-medium text-foreground">{t(req.leaveType)}</td>
                        <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                          {formatDate(req.startDate)} — {formatDate(req.endDate)}
                        </td>
                        <td className="py-3 px-4 text-end text-foreground">{r(req.numberOfDays)}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.bg} ${statusCfg.text}`}>
                            <StatusIcon className="h-3 w-3" />
                            {t(`employeeLeaves.status.${req.status}`)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-muted-foreground text-xs max-w-[200px] truncate" title={req.comments}>
                          {req.comments || '—'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(req.id); }}
                            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
                            title={t('View Details')}
                          >
                            {loadingRequestId === req.id ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Eye size={16} />
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredRequests.length > 10 && (
              <div className="p-3 border-t border-border text-center">
                <button
                  onClick={() => setShowAllRequests(!showAllRequests)}
                  className="inline-flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-medium"
                  data-testid="employee-leaves-show-more"
                >
                  {showAllRequests ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t('employeeLeaves.showLess')}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t('employeeLeaves.showAll', { count: filteredRequests.length })}
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Request Detail Modal */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          leaveTypes={leaveTypes}
          onClose={() => setSelectedRequest(null)}
        />
      )}

      {/* Upcoming Holidays Section */}
      <div className="bg-card border border-border rounded-lg" data-testid="employee-holidays-section">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <CalendarOff className="h-5 w-5 text-teal-600" />
            <h3 className="text-md font-semibold text-foreground">
              {t('employeeLeaves.upcomingHolidays')}
            </h3>
          </div>
        </div>

        {holidays.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <CalendarOff className="h-12 w-12 text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground text-sm">{t('employeeLeaves.noUpcomingHolidays')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {holidays.map((h, idx) => {
              const daysAway = daysUntil(h.date);
              return (
                <div
                  key={idx}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/30"
                  data-testid={`holiday-row-${idx}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                      <CalendarOff className="h-5 w-5 text-teal-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">{h.name}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(h.date)}</div>
                    </div>
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                    daysAway <= 7
                      ? 'bg-teal-100 text-teal-700'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {daysAway === 0
                      ? t('employeeLeaves.today')
                      : daysAway === 1
                        ? t('employeeLeaves.tomorrow')
                        : t('employeeLeaves.inNDays', { days: daysAway })}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const EmployeeLeavesAndHolidaysPage = (props: Props) => {
  return (
    <UserProfile>
      <Attendance>
        <EmployeeLeavesAndHolidays {...props} />
      </Attendance>
    </UserProfile>
  );
};

const mapStateToProps = (state: any) => ({
  userId: state.user.userId,
  role: state.user.role,
});

export { EmployeeLeavesAndHolidays };
export default connect(mapStateToProps)(EmployeeLeavesAndHolidaysPage);
